import React, { createContext, useContext, useEffect, useState } from 'react';
import { acquireTokenSilent, signIn, signOut as entraSignOut } from '@/integrations/auth/entra';
import { getMe } from '@/integrations/api';
import type { AppRole } from '@/integrations/api';

/**
 * Auth state, backed by Entra ID (Azure AD) instead of Supabase.
 *
 * `user` is a minimal shape ({ id, email }) so existing components that read
 * `user.id` / `user.email` keep working. The role comes from GET /api/me, which is
 * sourced from our own user_roles table.
 */
interface AuthUser {
  id: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  userRole: AppRole | null;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = async () => {
    const me = await getMe();
    setUser({ id: me.user_id, email: me.email, displayName: me.display_name });
    setUserRole(me.role);
  };

  // On load: if we already have a (silent) session, populate it. Otherwise stay
  // signed-out so the Auth page is shown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await acquireTokenSilent();
        if (token && !cancelled) {
          await loadMe();
        }
      } catch {
        // not signed in / no session yet
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithMicrosoft = async () => {
    await signIn();
    await loadMe();
  };

  const signOut = async () => {
    // Clear local app state even if the Microsoft logout popup is cancelled/errors, so
    // the user always lands back on the sign-in screen.
    try {
      await entraSignOut();
    } finally {
      setUser(null);
      setUserRole(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, userRole, signInWithMicrosoft, signOut, refresh: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
